import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import type { ActiveUpload } from "../../hooks/useFileUpload";

export interface ExplorerUploadStatusProps {
  upload: ActiveUpload | null;
  error: string | null;
  /** A 201 UploadResult with `renamedFrom`: "Saved as ..." (ADR 0011 section 6). */
  notice: string | null;
  retryFile: File | null;
  onCancel: () => void;
  onRetry: (file: File) => void;
}

/**
 * The in-flight progress bar, the post-upload notice, or the error/retry
 * banner, shown above the table while an upload is active or has just
 * settled. ADR 0011: once the body has been fully sent, the bar switches
 * to indeterminate "Checking" while the scanner works out a verdict (about
 * 1 to 2 minutes for a 2 GiB file); cancelling is only offered during the
 * byte transfer itself, not while checking.
 */
export function ExplorerUploadStatus({ upload, error, notice, retryFile, onCancel, onRetry }: ExplorerUploadStatusProps) {
  const checking = upload?.phase === "checking";
  const percent =
    upload && !checking && upload.total ? Math.min(100, Math.round((upload.loaded / upload.total) * 100)) : undefined;

  return (
    <Box sx={{ px: 2 }}>
      {upload && (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", py: 1 }} aria-live="polite">
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {checking
                ? `Checking ${upload.file.name} for malware. Large files can take up to 2 minutes.`
                : `Uploading ${upload.file.name}${percent !== undefined ? ` (${percent}%)` : ""}`}
            </Typography>
            <LinearProgress
              variant={checking || percent === undefined ? "indeterminate" : "determinate"}
              value={percent}
            />
          </Box>
          {!checking && (
            <IconButton aria-label={`Cancel upload of ${upload.file.name}`} size="small" onClick={onCancel}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {notice}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            retryFile && (
              <Button color="inherit" size="small" onClick={() => onRetry(retryFile)}>
                Retry
              </Button>
            )
          }
        >
          {error}
        </Alert>
      )}
    </Box>
  );
}
